import { Component, OnInit, OnDestroy, Inject, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Store, Action } from '@ngrx/store';
import { Subject } from 'rxjs/Subject';
import { Observable } from 'rxjs/Observable';
import { Router } from '@angular/router';
import { MatDialogRef, MatDialog } from "@angular/material";
import { Actions } from '@ngrx/effects';
import { Subscription } from 'rxjs';
import { MAT_DIALOG_DATA } from '@angular/material';
import * as actions from '../../pillar/dashboard/store/actions';
import { ModuleConfiguration } from '../vocabulary.module.config';
import { AddConceptVersionModel } from "../models/AddConceptVersionModel";
import { DuplicationFoundComponent } from "../components/duplication-found.component";
import * as fromStore from '../store/reducers';
import { AlertsComponent } from '../../pillar/alerts/components/alerts.component';
import { ACTION_ERROR, ActionError } from '../../pillar/common/actions/ActionError';
import { DUPLICATION_FOUND, DuplicationFoundAction } from '../../vocabulary/store/actions/DuplicationFoundAction';
import { CodeSystemVersionDashboardModel } from '../models/CodeSystemVersionDashboardModel'
import { ValueSetGroupModel } from '../models/ValueSetGroupModel';
import { ValueSetModel } from '../models/ValueSetModel';
import { VocabularyService } from "../services/VocabularyService";
import { RelateConceptVersionModel } from '../models/RelateConceptVersionModel';
import { CurrentUserService } from '../../security/services/CurrentUserService';

@Component({
  selector: 'brd-add-concept-version',
  templateUrl: './add-concept-version.component.html',
  styleUrls: ['./add-concept-version.component.scss']
})
export class AddConceptVersionComponent implements OnInit, OnDestroy {
  addForm: FormGroup;
  relateForm: FormGroup;
  eventUnsubscribe: Subscription;
  errors$: Observable<string[]>;
  destroyed$: Subject<boolean> = new Subject<boolean>();
  addActionIdentifier:string;
  @ViewChild(AlertsComponent)
  alerts: AlertsComponent;
  header: string;
  isMappedCodeSystem: boolean;
  actionSelected: boolean;
  actionTypeOptions: {description: string, value: string}[];
  actionType: string;
  valueSetGroups: ValueSetGroupModel[];
  valueSets: ValueSetModel[];
  selectedValueSetGroup: ValueSetGroupModel;
  selectedValueSetGroupDisplay: ValueSetGroupModel;
  selectedValueSet: ValueSetModel;
  concepts: ValueSetModel[];
  selectedConcept: any;
  canRelateVocabularyPermission = 'VocabularyModule.CodeSystem:RelateExisting';

  constructor(private store: Store<any>,
    public dialogRef: MatDialogRef<AddConceptVersionModel>,
    private formBuilder: FormBuilder,
    private router: Router,
    private actions$: Actions,
    private dialog: MatDialog,
    private vocabularyService: VocabularyService,
    private currentUserService: CurrentUserService,
    @Inject(MAT_DIALOG_DATA) public data: AddConceptVersionModel) {
    this.addForm = formBuilder.group({
      name: ['', Validators.required],
      code: ['', Validators.required]
    });
    this.relateForm = formBuilder.group({
      valueSetGroup: ['', Validators.required],
      valueSet: ['', Validators.required],
      concept: ['', Validators.required]
    });
    this.actionTypeOptions = [
      { description: 'Add New Concept', value: 'Add' },
      { description: 'Relate Existing Concept', value: 'Relate' }
    ];
    this.isMappedCodeSystem = data.isMappedCodeSystem;
    if(this.isMappedCodeSystem || !this.canRelateVocabulary()){
      this.header = "Add Concept";
      this.actionType = "Add";
      this.actionSelected = true;
    } else{
      this.header = "New / Existing Concept";
      this.actionSelected = false;
    }
  }

  ngOnInit() {
    this.eventUnsubscribe = this.actions$.ofType(actions.addSuccessType<AddConceptVersionModel>(ModuleConfiguration.moduleName, AddConceptVersionModel))
      .subscribe((a: actions.AddSuccessAction<AddConceptVersionModel>) => {
        this.router.navigateByUrl('concept-workspace/' + this.data.codeSystemVersionKey + '?conceptCode=' + a.payload.key);
        this.close();
      });

    this.eventUnsubscribe = this.actions$.ofType(actions.addSuccessType<RelateConceptVersionModel>(ModuleConfiguration.moduleName, RelateConceptVersionModel))
      .subscribe((a: actions.AddSuccessAction<RelateConceptVersionModel>) => {
        this.router.navigateByUrl('concept-workspace/' + this.data.codeSystemVersionKey + '?conceptCode=' + a.payload.key);
        this.close();
      });

    this.actions$.ofType(DUPLICATION_FOUND)
      .filter((a: DuplicationFoundAction) => this.addActionIdentifier && a.payload.model.code === this.generateFullCode(this.data))
      .takeUntil(this.destroyed$)
      .subscribe((p: DuplicationFoundAction) => {
        this.addActionIdentifier = undefined;
        this.duplicationFound(p.payload.model);
        this.close();
      });

    this.actions$.ofType(ACTION_ERROR)
      .filter((a: ActionError) => this.addActionIdentifier && a.payload.action.identifier === this.addActionIdentifier)
      .takeUntil(this.destroyed$)
      .subscribe((p: ActionError) => {
        this.addActionIdentifier = undefined;
        for (let error in p.payload.errors) {
          if (p.payload.errors.hasOwnProperty(error)) {
            this.alerts.addError(p.payload.errors[error]);
          }
        }
      });

    this.store.select(fromStore.getCodeSystemVersionSelection)
      .takeUntil(this.destroyed$)
      .subscribe((v: CodeSystemVersionDashboardModel) => {
        if (v) {
          this.valueSetGroups = v.valueSetGroups;
        }
      });
  }

  ngOnDestroy(): void {
    if (this.eventUnsubscribe) {
      this.eventUnsubscribe.unsubscribe();
    }
    this.destroyed$.next(true);
  }

  selectActionType(value){
    this.actionType = value;
    this.actionSelected = true;
    this.header = value + " Concept";
  }

  selectValueSetGroup( value: ValueSetGroupModel ) {
    this.selectedValueSetGroupDisplay = Object.assign([], value );
    this.selectedValueSetGroupDisplay.valueSets = value.valueSets.filter(vs => vs.conceptCode !== this.data.valueSetCode);
  }

  selectValueSet(value){
    this.selectedValueSet = value;
    this.vocabularyService.getConceptsForValueSet(0, 1000, null, null, this.selectedValueSet.conceptCode, this.data.codeSystemVersionKey, null)
    .takeUntil(this.destroyed$)
    .subscribe(c => {
      this.concepts = c.items;
    });
  }

  selectConcept(value){
      this.selectedConcept = value;
  }

  add() {
    var model = this.data;
    model.name = this.addForm.value.name;
    model.code = this.addForm.value.code;
    var addAction = new actions.AddAction(ModuleConfiguration.moduleName, AddConceptVersionModel, { model: model });
    this.addActionIdentifier = addAction.identifier;
    this.store.dispatch(new actions.AddAction(ModuleConfiguration.moduleName, AddConceptVersionModel, { model: model }));
  }

  relate(){
    var model = new RelateConceptVersionModel();
    model.code = this.selectedConcept.conceptCode;
    model.codeSystemVersionKey = this.data.codeSystemVersionKey;
    model.valueSetCode = this.data.valueSetCode;

    var relateAction = new actions.AddAction(ModuleConfiguration.moduleName, RelateConceptVersionModel, { model: model });
    this.addActionIdentifier = relateAction.identifier;
    this.store.dispatch(new actions.AddAction(ModuleConfiguration.moduleName, RelateConceptVersionModel, { model: model }));
  }

  close() {
    this.dialogRef.close();
  }

  duplicationFound(model: AddConceptVersionModel): void {
    this.dialog.open(DuplicationFoundComponent,
      {
        panelClass: 'customized-dialog',
        data:
        {
          code: model.code,
          name: model.name,
          valueSetCode: model.valueSetCode,
          codeSystemVersionKey: model.codeSystemVersionKey
        }
      });
  }

  generateFullCode(model: AddConceptVersionModel) {
    if (model.code.startsWith(model.valueSetCode)) {
      return model.code;
    }
    return model.valueSetCode + '.' + model.code;
  }

  canRelateVocabulary() {
    return this.currentUserService.hasPermission(this.canRelateVocabularyPermission);
  }
}



// WEBPACK FOOTER //
// ./src/app/vocabulary/components/add-concept-version.component.ts